import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, X, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollStatus();
  }, [sessionId]);

  const pollStatus = async () => {
    const maxAttempts = 12;
    const poll = async (attempt) => {
      if (attempt >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        setPaymentData(res.data);

        if (res.data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        setAttempts(attempt + 1);
        setTimeout(() => poll(attempt + 1), 2000);
      } catch (err) {
        setStatus('error');
      }
    };

    poll(0);
  };

  return (
    <div className="min-h-screen pt-24 flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full text-center"
      >
        {status === 'loading' && (
          <div data-testid="payment-loading">
            <Loader2 size={48} className="text-[#D4AF37] animate-spin mx-auto mb-6" />
            <h2 className="font-playfair text-2xl text-white mb-3">Processing Payment</h2>
            <p className="text-white/40 font-manrope text-sm">Verifying your payment... (Attempt {attempts + 1}/12)</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="payment-confirmed">
            <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-black" />
            </div>
            <h2 className="font-playfair text-3xl text-white mb-3">{paymentData?.booking_confirmed ? 'Booking Confirmed!' : 'Payment Successful!'}</h2>
            <p className="text-white/40 font-manrope text-sm mb-8">
              {paymentData?.booking_confirmed
                ? 'Your site survey is booked. A confirmation email with all your details is on its way.'
                : 'Thank you for your purchase. Our team will reach out to schedule your service.'}
            </p>
            {paymentData?.uan_code && (
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-sm p-6 mb-8" data-testid="uan-code-box">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-manrope font-semibold mb-2">Your UAN Activation Code</p>
                <p className="font-playfair text-3xl text-[#D4AF37] tracking-widest mb-3" data-testid="uan-code-value">{paymentData.uan_code}</p>
                <p className="text-white/40 font-manrope text-xs leading-relaxed">
                  Use this code to activate your Client Portal account and unlock premium features. It's also in your confirmation email.
                </p>
                <Link to="/portal" className="inline-block mt-4 text-[#D4AF37] font-manrope text-xs uppercase tracking-widest underline underline-offset-4" data-testid="uan-portal-link">
                  Set Up Your Client Account →
                </Link>
              </div>
            )}
            {paymentData && (
              <div className="bg-[#111113] border border-white/5 rounded-sm p-6 mb-8 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-white/40 font-manrope text-xs uppercase tracking-widest">Amount</span>
                  <span className="text-white font-manrope">${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40 font-manrope text-xs uppercase tracking-widest">Status</span>
                  <span className="text-[#D4AF37] font-manrope font-semibold">Paid</span>
                </div>
              </div>
            )}
            <Link
              to="/"
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-manrope font-bold text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-[#C5A059] transition-all duration-300"
              data-testid="back-home-btn"
            >
              Back to Home <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div data-testid="payment-error">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <X size={40} className="text-red-400" />
            </div>
            <h2 className="font-playfair text-2xl text-white mb-3">
              {status === 'timeout' ? 'Payment Status Unknown' : 'Payment Issue'}
            </h2>
            <p className="text-white/40 font-manrope text-sm mb-8">
              {status === 'timeout'
                ? 'We could not confirm your payment. Please check your email for confirmation.'
                : 'There was an issue with your payment. Please try again or contact us.'}
            </p>
            <Link
              to="/services"
              className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] font-manrope font-semibold text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-[#D4AF37] hover:text-black transition-all duration-300"
            >
              Try Again
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
